import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { appNoteStore } from "../model/services";
import { mapBlockTokens } from "../../shared/tokenIterators/mapBlockTokens";
import { Badge } from "../components/Badge";
import { storageStatsAtom } from "../model/atoms";
import logger from "../utils/logger";
import { noteLogs } from "../model/store/NoteLogs";

export const StatsSection = () => {
  const [storageStats, setStorageStats] = useAtom(storageStatsAtom);

  const [stats, setStats] = useState({
    noteCount: -1,
    wordCount: -1,
    totalHashtagCount: -1,
    uniqueHashtagCount: -1,
    linkCount: -1,
    imageCount: -1,
    totalCheckboxCount: -1,
    completedCheckboxCount: -1,
    referenceCount: -1,
  });

  useEffect(() => {
    if (storageStats !== null) return;
    if (navigator && navigator.storage) {
      const data = Promise.all([navigator.storage.estimate(), navigator.storage.persisted()]);
      data
        .then(([estimate, persisted]) => {
          setStorageStats({
            usage: estimate.usage || -1,
            quota: estimate.quota || -1,
            isPersistentStorage: persisted,
          });
        })
        .catch((error) => {
          logger.error("Failed to get storage stats", {
            error,
          });
        });
    } else {
      setStorageStats({
        isPersistentStorage: false,
        usage: -1,
        quota: -1,
      });
    }
  }, [setStorageStats, storageStats]);

  useEffect(() => {
    if (stats.noteCount > 0) return;
    let wordCount = 0;
    let totalHashtagCount = 0;
    let linkCount = 0;
    let imageCount = 0;
    let totalCheckboxCount = 0;
    let completedCheckboxCount = 0;
    let referenceCount = 0;
    const nodeIterator = mapBlockTokens((blockToken) => {
      if (blockToken.type === "paragraph") {
        blockToken.content.forEach((inToken) => {
          if (inToken.type === "text") {
            wordCount += inToken.content.split(" ").length;
          } else if (inToken.type !== "checkbox" && inToken.type !== "image") {
            wordCount += 1;
          }
          // do not make this an `else if` since we want too count it in the
          // word count above
          if (inToken.type === "hashtag") {
            totalHashtagCount += 1;
          } else if (inToken.type === "link") {
            linkCount += 1;
          } else if (inToken.type === "image") {
            imageCount += 1;
          } else if (inToken.type === "checkbox") {
            totalCheckboxCount += 1;
            if (inToken.isChecked) completedCheckboxCount += 1;
          } else if (inToken.type === "spaceship") {
            referenceCount += 1;
          }
        });
      }
      if (blockToken.type === "codeblock") {
        blockToken.content.forEach((inToken) => {
          wordCount += inToken.content.split(" ").length;
        });
      }
    });

    const notes = appNoteStore.getAll();
    const uniqueHashtagCount = appNoteStore.hashtags.getAllItems().length;

    notes.forEach((note) => {
      note.tokens.forEach(nodeIterator);
    });
    setStats({
      wordCount,
      noteCount: notes.length,
      totalHashtagCount,
      uniqueHashtagCount,
      linkCount,
      imageCount,
      totalCheckboxCount,
      completedCheckboxCount,
      referenceCount,
    });
  }, [stats.noteCount]);

  const wordsPerNote =
    stats.noteCount === 0
      ? 0
      : (stats.wordCount / stats.noteCount).toLocaleString(undefined, {
          maximumFractionDigits: 1,
        });

  return (
    <>
      <p>
        {stats.noteCount.toLocaleString()}
        {" notes with "}
        {stats.wordCount.toLocaleString()}
        {" words total "}
        {`(${wordsPerNote} words/note)`}
      </p>
      <p>
        {stats.uniqueHashtagCount.toLocaleString()}
        {" hashtags used "}
        {stats.totalHashtagCount.toLocaleString()}
        {" times total"}
      </p>
      {stats.referenceCount ? (
        <p>
          {stats.referenceCount.toLocaleString()}
          {" references between notes"}
        </p>
      ) : null}
      {stats.totalCheckboxCount ? (
        <p>
          {stats.completedCheckboxCount.toLocaleString()}
          {" / "}
          {stats.totalCheckboxCount.toLocaleString()} todo items completed
        </p>
      ) : null}
      {stats.imageCount ? (
        <p>
          {stats.imageCount.toLocaleString()}
          {" images"}
        </p>
      ) : null}
      {stats.linkCount ? (
        <p>
          {stats.linkCount.toLocaleString()}
          {" links"}
        </p>
      ) : null}
      {storageStats && storageStats.usage > -1 ? (
        <p>
          {`${(storageStats.usage / 1024 / 1024).toFixed(2)}MB`}
          {" / "}
          {`${(storageStats.quota / 1024 / 1024).toFixed(2)}MB`}
          {` (${(storageStats.usage / storageStats.quota).toFixed(2)}%) `}
          <Badge
            type={storageStats.isPersistentStorage ? "safe" : "danger"}
            text={storageStats.isPersistentStorage ? "Persistent" : "Non Persistent"}
          />
        </p>
      ) : null}
      {noteLogs.allLogs.length > 0 && (
        <>
          <details>
            <summary>Show last {noteLogs.allLogs.length} authored notes</summary>
            <table>
              <thead>
                <tr>
                  <th>Note ID</th>
                  <th>Note Preview</th>
                </tr>
              </thead>
              <tbody>
                {noteLogs.allLogs.reverse().map((log) => {
                  const note = appNoteStore.get(log.i);
                  if (!note) {
                    return (
                      <tr key={log.i}>
                        <td>{log.i}</td>
                        <td>Missing note</td>
                      </tr>
                    );
                  }
                  const preview = appNoteStore.getNotePreview(note.id);
                  return (
                    <tr key={log.i}>
                      <td>{log.i}</td>
                      <td>{preview.length > 50 ? preview.slice(0, 50) + "..." : preview}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </details>
        </>
      )}
    </>
  );
};
